import { useEffect, useState } from "react";
import { Loader2, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DateInput } from "@/components/ui/date-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FIELD_LABELS,
  REVENUE_FIELDS,
  formatFt,
} from "@/pages/daily_revenues/helpers";

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function DailyRevenueFormDialog({
  open,
  onOpenChange,
  initial,        // existing entry or null
  onSaved,
}) {
  const isEdit = !!initial?._id;
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [date, setDate] = useState(todayIso());
  const [amounts, setAmounts] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const tpl = templates.find((t) => t._id === templateId) || null;
  const enabledFields =
    initial?.fields_snapshot ||
    tpl?.fields ||
    {};

  useEffect(() => {
    if (!open) return;
    if (initial?._id) {
      setTemplateId(initial.template_id || "");
      setDate(initial.date || todayIso());
      const a = {};
      REVENUE_FIELDS.forEach((f) => {
        a[f] = String(initial.amounts?.[f] ?? "");
      });
      setAmounts(a);
      setNotes(initial.notes || "");
    } else {
      setTemplateId("");
      setDate(todayIso());
      setAmounts({});
      setNotes("");
    }
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/revenue-templates");
        setTemplates((data || []).filter((t) => t.active !== false));
      } catch {/* silent */}
    })();
  }, [open]);

  const total = REVENUE_FIELDS.reduce((sum, f) => {
    if (!enabledFields[f]) return sum;
    return sum + (Number(amounts[f]) || 0);
  }, 0);

  const onSubmit = async () => {
    if (!isEdit && !templateId) {
      toast.error("Válassz sablont.");
      return;
    }
    if (!date) {
      toast.error("Add meg a dátumot.");
      return;
    }
    setSaving(true);
    try {
      const amountPayload = {};
      REVENUE_FIELDS.forEach((f) => {
        if (enabledFields[f]) {
          amountPayload[f] = Number(amounts[f]) || 0;
        }
      });
      let resp;
      if (isEdit) {
        resp = await api.patch(`/daily-revenues/${initial._id}`, {
          amounts: amountPayload,
          notes: notes.trim() || null,
        });
        toast.success("Bejegyzés frissítve.");
      } else {
        resp = await api.post("/daily-revenues", {
          template_id: templateId,
          date,
          amounts: amountPayload,
          notes: notes.trim() || null,
        });
        toast.success("Bejegyzés rögzítve.");
      }
      onSaved?.(resp.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Mentés sikertelen."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="daily-revenue-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarPlus className="h-4 w-4" />
            {isEdit ? "Bejegyzés szerkesztése" : "Új napi bevétel"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Szerkeszd a részösszegeket. Az ellenőrzés indításával zárolódik."
              : "Válaszd ki a sablont (telephelyt), majd add meg a részösszegeket."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Sablon / Telephely *</Label>
              <Select
                value={templateId}
                onValueChange={setTemplateId}
                disabled={isEdit}
              >
                <SelectTrigger data-testid="entry-template-select">
                  <SelectValue placeholder="Válassz sablont…" />
                </SelectTrigger>
                <SelectContent>
                  {templates.length === 0 ? (
                    <div className="px-3 py-3 text-center text-xs text-muted-foreground">
                      Nincs aktív sablon. Hozz létre egyet a Sablonok fülön.
                    </div>
                  ) : (
                    templates.map((t) => (
                      <SelectItem key={t._id} value={t._id}>
                        {t.site_name}
                        {t.company_name ? ` · ${t.company_name}` : ""}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="date">Dátum *</Label>
              <DateInput
                id="date"
                value={date}
                onChange={setDate}
                disabled={isEdit}
                clearable={false}
                data-testid="entry-date"
              />
            </div>
          </div>

          {Object.keys(enabledFields).length === 0 && !isEdit && (
            <p className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-4 text-center text-xs text-muted-foreground">
              Válassz először sablont, hogy lásd milyen részösszegeket kell rögzíteni.
            </p>
          )}

          {Object.keys(enabledFields).length > 0 && (
            <div className="space-y-2">
              <Label>Részösszegek</Label>
              <div className="grid gap-2 sm:grid-cols-2">
                {REVENUE_FIELDS.filter((f) => enabledFields[f]).map((f) => (
                  <div key={f} className="space-y-1">
                    <Label className="text-[11px]" htmlFor={`amt-${f}`}>
                      {FIELD_LABELS[f]} összesen
                    </Label>
                    <Input
                      id={`amt-${f}`}
                      type="number"
                      min="0"
                      step="1"
                      value={amounts[f] || ""}
                      onChange={(e) =>
                        setAmounts((p) => ({ ...p, [f]: e.target.value }))
                      }
                      placeholder="0"
                      data-testid={`amt-${f}`}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center justify-between rounded-lg border border-border bg-muted/20 px-4 py-3">
                <span className="text-sm font-medium">Összes bevétel</span>
                <span
                  className="text-base font-bold tabular-nums"
                  data-testid="entry-total"
                >
                  {formatFt(total)}
                </span>
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="notes">Megjegyzés</Label>
            <Textarea
              id="notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Pl. esti műszak, akciós nap, stb."
              data-testid="entry-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Mégse
          </Button>
          <Button onClick={onSubmit} disabled={saving} data-testid="save-entry">
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {isEdit ? "Mentés" : "Rögzítés"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
